import { type LucideIcon, ArrowLeft, ArrowRight, Save, CornerUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import WizardStepper from './WizardStepper';

interface WizardLayoutProps {
  children: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  stepTitle: string;
  stepDescription: string;
  onNext: () => void;
  onBack: () => void;
  onStepClick: (step: number) => void;
  completedSteps: number[];
  steps: { label: string; icon: LucideIcon }[];
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  nextLabel?: string;
  hideNext?: boolean;
  savedAt?: string | null;
  // Bookmark surface: when the user jumps backwards via the stepper,
  // the parent stashes their previous step and we render a single-click
  // "Return to {label}" pill so they don't have to Continue through
  // every intermediate step to get back.
  returnToStep?: number | null;
  returnToStepLabel?: string | null;
  onReturnToStep?: () => void;
}

export default function WizardLayout({
  children,
  currentStep,
  totalSteps,
  stepTitle,
  stepDescription,
  onNext,
  onBack,
  onStepClick,
  completedSteps,
  steps,
  isNextDisabled = false,
  isNextLoading = false,
  nextLabel,
  hideNext = false,
  savedAt,
  returnToStep = null,
  returnToStepLabel = null,
  onReturnToStep,
}: WizardLayoutProps) {
  const showReturnPill = returnToStep != null && returnToStep !== currentStep && !!onReturnToStep;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const resolvedNextLabel = nextLabel ?? (isLastStep ? 'Create Site' : 'Continue');

  const formattedSavedAt = savedAt
    ? new Date(savedAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="max-w-4xl mx-auto pb-24 md:pb-8">
      {/* Stepper */}
      <div className="mb-8">
        <WizardStepper
          steps={steps}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={onStepClick}
        />
      </div>

      {/* Step header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-text-primary">
              {stepTitle}
            </h1>
            <p className="text-sm text-text-secondary mt-1">{stepDescription}</p>
          </div>

          {/* Auto-save indicator */}
          {formattedSavedAt && (
            <div className="hidden md:flex items-center gap-1.5 text-xs text-text-muted shrink-0 mt-1">
              <Save className="w-3.5 h-3.5" />
              <span>Draft saved {formattedSavedAt}</span>
            </div>
          )}
        </div>
      </div>

      {/* Return-to-step pill — shows when the user jumped backward via the
          stepper. One click takes them back to where they left off. */}
      {showReturnPill && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-[var(--color-primary-200)] bg-[var(--color-primary-50)] px-4 py-2.5 text-sm">
          <span className="text-text-secondary">
            You jumped back from <strong className="text-text-primary">{returnToStepLabel}</strong>.
          </span>
          <button
            type="button"
            onClick={onReturnToStep}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary-600)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-primary-700)] transition-colors"
          >
            <CornerUpRight className="w-3.5 h-3.5" />
            Return to {returnToStepLabel}
          </button>
        </div>
      )}

      {/* Content area */}
      <div className="space-y-6">{children}</div>

      {/* Desktop bottom navigation */}
      <div className="hidden md:flex items-center justify-between mt-8 pt-6 border-t border-stone-200">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={isFirstStep}
          className={cn(isFirstStep && 'invisible')}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        <span className="text-sm text-text-muted">
          Step {currentStep + 1} of {totalSteps}
        </span>

        {!hideNext && (
          <Button
            variant="boutique"
            onClick={onNext}
            disabled={isNextDisabled}
            isLoading={isNextLoading}
          >
            {resolvedNextLabel}
            {!isLastStep && <ArrowRight className="w-4 h-4" />}
          </Button>
        )}
      </div>

      {/* Mobile sticky bottom bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 px-4 py-3 z-40">
        <div className="flex items-center gap-3">
          {!isFirstStep && (
            <Button variant="ghost" size="sm" onClick={onBack} className="shrink-0">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          )}

          <div className="flex-1 flex items-center justify-center">
            {formattedSavedAt && (
              <span className="text-xs text-text-muted flex items-center gap-1">
                <Save className="w-3 h-3" />
                Saved {formattedSavedAt}
              </span>
            )}
          </div>

          {!hideNext && <Button
            variant="boutique"
            size="sm"
            onClick={onNext}
            disabled={isNextDisabled}
            isLoading={isNextLoading}
            className="shrink-0"
          >
            {resolvedNextLabel}
            {!isLastStep && <ArrowRight className="w-4 h-4" />}
          </Button>}
        </div>
      </div>
    </div>
  );
}
